import { useEffect, useMemo, useRef, useState } from 'react';
import { worker } from '../../../../app.worker';
import WebWorker from 'src/WebWorker';
import { ChartItemType, MainObjectType, TimestampsTypes, ValuesTypes } from './types';
import { SelectedInputType } from '../../types/selected-input';

type Props = {
  timestamps: TimestampsTypes;
  selectedTags: SelectedInputType[];
  startDate: string;
  endDate: string;
  aggregationTypePayload: 'two_hour' | 'five_min' | 'thirty_min' | null;
};

export const useChartData = ({
  timestamps,
  selectedTags,
  startDate,
  endDate,
  aggregationTypePayload,
}: Props) => {
  const [data, setData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const prevDepsRef = useRef({ timestamps, selectedTags, aggregationTypePayload });
  if (
    prevDepsRef.current.timestamps !== timestamps ||
    prevDepsRef.current.selectedTags !== selectedTags ||
    prevDepsRef.current.aggregationTypePayload !== aggregationTypePayload
  ) {
    prevDepsRef.current = { timestamps, selectedTags, aggregationTypePayload };
    if (selectedTags && selectedTags.length > 0 && !isLoading) {
      setIsLoading(true);
    }
  }

  useEffect(() => {
    if (!selectedTags || selectedTags.length === 0) return;

    setIsLoading(true);
    let completedRequests = 0;
    const totalRequests = selectedTags.reduce(
      (sum, tag) => sum + (tag.statistic === 'Min-Average-Max' ? 3 : 1),
      0
    );
    const collectedData: any[] = [];

    const webWorker = new WebWorker(worker);

    webWorker.onmessage = (e: MessageEvent) => {
      const { status, error } = e.data;

      if (status === 'success') {
        collectedData.push({ apiResponse: e.data.response, requestTag: e.data.requestTag });
      } else {
        console.error(error);
      }
      completedRequests++;
      if (completedRequests === totalRequests) {
        setData(collectedData);
        setIsLoading(false);
      }
    };

    selectedTags?.forEach((tag, tagIndex) => {
      if (!tag?.gatewayId) return;

      const baseMsg = {
        gatewayId: tag.gatewayId,
        startDate: timestamps.fromDate ? startDate : null,
        endDate: timestamps.toDate ? endDate : null,
        measurements: tag.inputId,
        aggregationType: tag.aggregation === 'Auto' ? aggregationTypePayload : tag.aggregation,
        apiUrl: import.meta.env.VITE_STREAMETRIC_API,
      };

      // requestTag is keyed by the row's index (not inputId) so responses can be matched
      // back to the exact row even when the same tag is added more than once.
      if (tag.statistic === 'Min-Average-Max') {
        (['min', 'average', 'max'] as const).forEach((variant) => {
          webWorker.postMessage({
            ...baseMsg,
            isOnChange: false,
            statistics: variant,
            requestTag: `${tagIndex}__${variant}`,
          });
        });
      } else {
        const isOnChange = tag.statistic === 'On-change';
        webWorker.postMessage({
          ...baseMsg,
          isOnChange,
          statistics: isOnChange || tag.statistic === 'Auto' ? null : tag.statistic,
          requestTag: `${tagIndex}`,
        });
      }
    });

    return () => {
      webWorker.terminate();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timestamps, selectedTags, aggregationTypePayload]);

  const formatedNewData = useMemo(
    () =>
      data.flatMap(
        (item: {
          apiResponse: ChartItemType;
          requestTag?: string;
        }): { tagId: string; values: ValuesTypes }[] => {
          const { apiResponse, requestTag } = item;
          const dataObj = Object.entries(apiResponse?.data);
          const dataEntry = dataObj[0];
          const valuesObj: MainObjectType = dataEntry[1];

          if (!requestTag) return [];

          const [tagIndex, variant] = requestTag.split('__');

          if (variant) {
            const tagSuffix = variant === 'average' ? 'avg' : variant;
            const values = valuesObj.statistics?.[variant] ?? valuesObj.values;
            return [{ tagId: `${tagIndex}__${tagSuffix}`, values }];
          }

          const matchedTag = selectedTags[Number(tagIndex)];
          const statistic = matchedTag?.statistic;
          const values =
            statistic && statistic !== 'Auto' && valuesObj.statistics?.[statistic]
              ? valuesObj.statistics[statistic]
              : valuesObj.values;

          return [{ tagId: tagIndex, values }];
        }
      ),
    [data, selectedTags]
  );

  return { data: formatedNewData, isLoading };
};
